import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BarChart3Icon, MapPinIcon, CalendarIcon, ZapIcon, ChevronDownIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'How did Viridia help TFP Landscaping get more leads?',
  answer: 'We implemented our Business Essentials package, which includes GEO-tuned Yelp Ads and an AI-powered Request a Quote (RAQ) system. By responding instantly to inquiries instead of waiting 4+ hours, TFP was able to capture leads before competitors could.'
}, {
  question: 'What is the Business Essentials package?',
  answer: 'The Business Essentials package combines targeted ad management (like Yelp Ads) with our AI lead conversion system. It ensures that the traffic generated by ads is immediately engaged and nurtured into booked appointments.'
}, {
  question: 'How does AI book estimates automatically?',
  answer: "Our AI is integrated directly into messaging platforms like Yelp. When a customer requests a quote, the AI instantly replies, asks qualifying questions based on the business's services, and provides a booking link or schedules the estimate directly on the calendar."
}, {
  question: 'What results did TFP Landscaping see in 30 days?',
  answer: 'In just 30 days, TFP Landscaping saw a 284% increase in leads, totaling 242 Yelp leads. They achieved a 39.95% close rate on those leads, with 28 estimates booked end-to-end entirely by the AI system.'
}, {
  question: 'How did Viridia help Finish Coat Painting increase revenue?',
  answer: 'Using the Never Miss a Lead package, Viridia implemented AI-powered messaging to handle inbound Yelp conversations instantly. In 30 days, the AI booked 29 estimates, generating $15.05k in closed-won revenue from end-to-end AI bookings alone - not including revenue from phone calls or website leads.'
}, {
  question: 'Can AI follow-up actually close jobs for contractors?',
  answer: 'Yes. The Finish Coat Painting case study proves that AI follow-up does more than just respond fast - it helps close real jobs. By engaging high-intent local demand immediately, AI turns inquiries into booked estimates and closed revenue.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1030">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1031">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-1032">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-1033" />
      </button>
      <AnimatePresence data-id="element-1034">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1035">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1036">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function CaseStudiesPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-1037">
      <SEOHead title="Case Studies | Real Results for Local Businesses | Viridia Analytics" description="See how Viridia Analytics helps home service businesses like TFP Landscaping and Finish Coat Painting capture more leads, automate follow-up, and book more jobs." canonical="/case-studies" keywords="marketing case studies, HVAC marketing results, plumber marketing success, local business growth" faq={faqs} data-id="element-1038" />

      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-1039">
        <ScrollReveal data-id="element-1040">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-1041">
            PROVEN RESULTS
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-1042">
            Case Studies: Real Leads, Real Estimates,{' '}
            <span className="text-viridia" data-id="element-1043">Real Revenue</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-1044">
            These are real client outcomes from AI follow-up, Yelp Ads, and
            local lead conversion systems. We build systems that actually
            generate revenue for local service businesses.
          </p>
        </ScrollReveal>
      </section>

      {/* CASE STUDIES HUB */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1045">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1046">
          {/* TFP Landscaping Card */}
          <ScrollReveal delay={0.1} data-id="element-1047">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl h-full flex flex-col group hover:border-blue-500/50 transition-colors" data-id="element-1048">
              <div className="bg-gray-900 p-8 relative overflow-hidden border-b border-gray-800" data-id="element-1049">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px]" data-id="element-1050"></div>
                <div className="relative z-10" data-id="element-1051">
                  <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1052">
                    TFP Landscaping
                  </h2>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1053">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1054">
                      Landscaping
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1055">
                      <MapPinIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-1056" />
                      Greater Phoenix
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1057">
                      <ZapIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-1058" />
                      Business Essentials
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1059">
                      <CalendarIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-1060" />
                      Client since July 2025
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1061">
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1062">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-1063">
                        284%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1064">Lead Increase</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1065">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-1066">
                        242
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1067">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1068">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-1069">
                        28
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1070">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1071">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-1072">
                        39.9%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1073">Close Rate</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 flex-grow flex flex-col justify-between" data-id="element-1074">
                <p className="text-gray-400 leading-relaxed mb-8" data-id="element-1075">
                  One-man operation went from 4+ hour response times to instant
                  AI follow-up. Using the Business Essentials package, TFP
                  captured high-intent local demand and booked estimates
                  automatically.
                </p>
                <Link to="/case-studies/tfp-landscaping" className="inline-flex items-center text-blue-400 font-bold hover:text-blue-300 transition-colors" data-id="element-1076">
                  Read Full Case Study{' '}
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1077" />
                </Link>
              </div>
            </div>
          </ScrollReveal>

          {/* Finish Coat Painting Card */}
          <ScrollReveal delay={0.2} data-id="element-1078">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl h-full flex flex-col group hover:border-orange-500/50 transition-colors" data-id="element-1079">
              <div className="bg-gray-900 p-8 relative overflow-hidden border-b border-gray-800" data-id="element-1080">
                <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-[80px]" data-id="element-1081"></div>
                <div className="relative z-10" data-id="element-1082">
                  <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1083">
                    Finish Coat Painting
                  </h2>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-1084">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1085">
                      Painting
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-1086">
                      <ZapIcon className="w-3 h-3 mr-1 text-orange-500" data-id="element-1087" />
                      Never Miss a Lead
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1088">
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1089">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-1090">
                        29
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1091">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1092">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-1093">
                        $15.05k
                      </p>
                      <p className="text-gray-400 text-xs font-semibold" data-id="element-1094">
                        Closed-Won (AI Only)
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1095">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-1096">
                        138
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1097">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-1098">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-1099">
                        64%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-1100">Lead Growth</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 flex-grow flex flex-col justify-between" data-id="element-1101">
                <p className="text-gray-400 leading-relaxed mb-8" data-id="element-1102">
                  AI handled inbound Yelp conversations and booked estimates
                  while the team was on job sites. The Never Miss a Lead package
                  turned missed opportunities into $15.05k of closed revenue
                  from end-to-end AI bookings alone.
                </p>
                <Link to="/case-studies/finish-coat-painting" className="inline-flex items-center text-orange-500 font-bold hover:text-orange-400 transition-colors" data-id="element-1103">
                  Read Full Case Study{' '}
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1104" />
                </Link>
              </div>
            </div>
          </ScrollReveal>

          {/* More Coming Soon Card */}
          <ScrollReveal delay={0.3} data-id="element-1105">
            <div className="bg-viridia-surface/50 border border-gray-800 border-dashed rounded-2xl p-8 h-full flex flex-col justify-center items-center text-center relative overflow-hidden min-h-[300px]" data-id="element-1106">
              <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4" data-id="element-1107">
                <BarChart3Icon className="w-8 h-8 text-gray-500" data-id="element-1108" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-1109">
                More Success Stories Coming Soon
              </h3>
              <p className="text-sm text-gray-400 max-w-xs" data-id="element-1110">
                We are currently compiling data for our latest client wins.
                Check back soon for more documented results.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1111">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1112">
          <ScrollReveal data-id="element-1113">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1114">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1115">
              Everything you need to know about working with Viridia Analytics.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1116">
          <ScrollReveal delay={0.2} data-id="element-1117">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1118">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1119" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden mt-12" data-id="element-1120">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-1121"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1122">
          <ScrollReveal data-id="element-1123">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1124">
              Want to Be Our Next Case Study?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1125">
              Book a free strategy call and let's build a system that captures
              leads, responds faster, and books more jobs for your business.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-1126">
              Book Your Free Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1127" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}